import { toast } from "react-toastify";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { updateMemberAPI } from "../../actioncreators/actioncreators";
import { IconButton, Chip, Tooltip, FormControl, Select as MuiSelect, OutlinedInput, MenuItem, Checkbox, ListItemText, Box } from "@mui/material";
import { XCircle, Edit, CheckCircle, Trash2, UserPlus, Users, Star } from "lucide-react";
import { fetchMemberDetails, fetchMemberApi, changeManagerAPI, fetchSupervisors, fetchUnassignedUsersAPI, assignMembersAPI, fetchUserManagersAPI } from "../../actioncreators/actioncreators";
import { useSelector } from "react-redux";
import { useSuspension } from "../../contexts/SuspensionContext";
import MultiManagerDialog from "./MultiManagerDialog";

// Helper function to format date from ISO to YYYY-MM-DD for date input
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (e) {
    return '';
  }
};

// Avatar component for manager (updated for larger size)
const ManagerAvatar = ({ user }) => {
  const [imageError, setImageError] = useState(false);
  const getInitials = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return "";
    const names = fullName.trim().split(" ").filter(Boolean);
    if (names.length === 0) return "";
    if (names.length > 1) {
      return (names[0][0] ? names[0][0].toUpperCase() : "") + (names[1][0] ? names[1][0].toUpperCase() : "");
    }
    return names[0][0] ? names[0][0].toUpperCase() : "";
  };
  if (!user?.user_image || imageError) {
    return (
      <div className="rounded-circle bg-gray-200 d-flex align-items-center justify-content-center" style={{ width: 100, height: 100, fontSize: 36, fontWeight: 600, color: '#555' }}>
        {getInitials(user?.full_name)}
      </div>
    );
  }
  return (
    <img
      src={`data:image/jpeg;base64,${user.user_image}`}
      alt={user.full_name}
      style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover' }}
      onError={() => setImageError(true)}
    />
  );
};

const MemberDetailsPage = () => {
  // Suspension check
  const { isSuspended } = useSuspension();

    // Collapsible state for Work Schedule and Location Assignment section
    const [workScheduleCollapsed, setWorkScheduleCollapsed] = React.useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState({
    full_name: '',
    email: '',
    mobile: '',
    designation: '',
    org_name: '',
    role_name: '',
    supervisor_ids: [],
    supervisor_name: '',
    dob: '',
    aadhaar_authentication: false,
  });
  // Accept both selectedMember and selectedManager from state for compatibility
  const [selectedManager, setSelectedManager] = useState(location.state?.selectedMember || location.state?.selectedManager || null);
  // The Super Admin's org/sub-org selection AT THE TIME the manager was
  // clicked from the Users tab (Member.jsx). Needed so "Supervised Members"
  // is scoped to the same context the manager was viewed from — a manager
  // assigned to a sub-org still manages root-level reports too, and which
  // set to show depends on whether we arrived here from the root org tab or
  // the sub-org's own tab.
  const viewedOrgId = location.state?.viewedOrgId || null;
  const viewedSubOrgId = location.state?.viewedSubOrgId || null;
  const [isChangeManagerMode, setIsChangeManagerMode] = useState(false);
  const [selectedMembersForChange, setSelectedMembersForChange] = useState([]);
  const [newManagerId, setNewManagerId] = useState("");
  const [isAssignManagerMode, setIsAssignManagerMode] = useState(false);
  const [selectedMembersToAssign, setSelectedMembersToAssign] = useState([]);
  const [unassignedUsers, setUnassignedUsers] = useState([]);
  const [isLoadingUnassigned, setIsLoadingUnassigned] = useState(false);
  const [managerInfo, setManagerInfo] = useState(null);
  // Store the full API response for member details
  const [memberDetails, setMemberDetails] = useState(null);


  // State for dynamic manager list
  const [managersList, setManagersList] = useState([]);
  const [isManagersLoading, setIsManagersLoading] = useState(false);
  const [managersError, setManagersError] = useState(null);

  // State for multi-manager support
  const [isMultiManagerDialogOpen, setIsMultiManagerDialogOpen] = useState(false);
  const [userManagers, setUserManagers] = useState([]);
  const [isLoadingUserManagers, setIsLoadingUserManagers] = useState(false);
  
  // State for supervisors dropdown in edit mode.
  // orgId must not depend solely on state.fetchOrgId: that slice is only
  // populated as a side effect of the Members list page's fetchMembers()
  // call, so landing on member-details directly (deep link, refresh, or a
  // fresh session that never visited the Members list) left it empty and
  // broke every org-scoped fetch here (e.g. "Manage Managers" always showing
  // "No available managers found" even when managers exist in the DB).
  const loginResponse = useSelector((state) => state.loginResponse);
  const orgIdObj = useSelector((state) => state.fetchOrgId);
  const orgId =
    selectedManager?.org_id ||
    loginResponse?.org_id ||
    loginResponse?.orgId ||
    orgIdObj?.orgId;
  const [supervisorsList, setSupervisorsList] = useState([]);
  const [isSupervisorsLoading, setIsSupervisorsLoading] = useState(false);
  // Fetch managers when dropdown is focused
  const handleManagersDropdownFocus = async () => {
    if (managersList.length === 0 && !isManagersLoading) {
      setIsManagersLoading(true);
      setManagersError(null);
      try {
        // Fetch all members, then filter for managers
        const response = await fetchMemberApi({ pageNumber: 1 });
        const data = response?.data || response;
        let allMembers = [];
        if (Array.isArray(data?.summaryData)) {
          allMembers = data.summaryData;
        } else if (Array.isArray(data?.data)) {
          allMembers = data.data;
        } else if (Array.isArray(data)) {
          allMembers = data;
        }
        const managers = allMembers.filter((m) => m.role_name === "MANAGER");
        setManagersList(managers);
      } catch (err) {
        setManagersError("Failed to load managers");
      } finally {
        setIsManagersLoading(false);
      }
    }
  };

  useEffect(() => {
    // Always fetch member details for the selected manager on mount/redirect
    const loadMemberDetails = async () => {
      if (selectedManager?.user_id) {
        try {
          // API expects { userId: ... }; org_id/sub_org_id tell the backend
          // which context to scope "Supervised Members" to (see viewedOrgId
          // comment above).
          const detailsPayload = { userId: selectedManager.user_id };
          if (viewedOrgId) detailsPayload.org_id = viewedOrgId;
          if (viewedSubOrgId) detailsPayload.sub_org_id = viewedSubOrgId;
          const response = await fetchMemberDetails(detailsPayload);
          // Support both axios and fetch style responses
          const responseData = response?.data || response;
          // Extract teamMembers or supervisedMembers from userData if present, else fallback to other shapes
          let membersList = [];
          setMemberDetails(responseData); // Save the full API response for later use
          if (Array.isArray(responseData?.userData?.supervisedMembers)) {
            // For LOCATION_MANAGER - use supervisedMembers
            membersList = responseData.userData.supervisedMembers;
            setManagerInfo(responseData.userData);
          } else if (Array.isArray(responseData?.userData?.teamMembers)) {
            // For ORG_ADMIN - use teamMembers
            membersList = responseData.userData.teamMembers;
            setManagerInfo(responseData.userData);
          } else {
            membersList = Array.isArray(responseData)
              ? responseData
              : Array.isArray(responseData.members)
                ? responseData.members
                : Array.isArray(responseData.data)
                  ? responseData.data
                  : [];
            setManagerInfo(null);
          }
          setMembers(membersList);
        } catch (error) {
          console.error("Error fetching member details:", error);
          setMembers([]);
          setManagerInfo(null);
        }
      } else if (location.state?.allMembers) {
        // Fallback: if no manager, but allMembers provided in state
        setMembers(location.state.allMembers);
        setManagerInfo(null);
      } else {
        // If no state, fetch all members as a fallback (or show a message)
        try {
          const response = await fetchMemberApi({ pageNumber: 1 });
          const data = response?.data || response;
          let allMembers = [];
          if (Array.isArray(data?.summaryData)) {
            allMembers = data.summaryData;
          } else if (Array.isArray(data?.data)) {
            allMembers = data.data;
          } else if (Array.isArray(data)) {
            allMembers = data;
          }
          setMembers(allMembers);
          setManagerInfo(null);
        } catch (err) {
          setMembers([]);
          setManagerInfo(null);
        }
      }
    };
    loadMemberDetails();
  }, [selectedManager, location.state]);

  useEffect(() => {
    // If selectedManager is present, set editValues.
    // supervisor_ids here is a placeholder only: neither the member-list nor
    // member-details fetch ever returns a real supervisor_ids array, so this
    // falls back to the single legacy supervisor_id. It gets corrected by the
    // effect below once the authoritative multi-manager list (userManagers,
    // fetched via fetchUserManagersAPI) loads. Without that correction, saving
    // any unrelated field (e.g. toggling Aadhaar Authentication) would send
    // this 1-manager placeholder as new_data.supervisor_ids, and the backend
    // replaces ALL of the member's manager assignments with it -- silently
    // dropping every manager beyond the first for every edit.
    if (selectedManager) {
      setEditValues({
        full_name: selectedManager.full_name || '',
        email: selectedManager.email || '',
        mobile: selectedManager.mobile || '',
        designation: selectedManager.designation || '',
        org_name: selectedManager.org_name || '',
        role_name: selectedManager.role_name || '',
        supervisor_ids: selectedManager.supervisor_ids && Array.isArray(selectedManager.supervisor_ids)
          ? selectedManager.supervisor_ids
          : (selectedManager.supervisor_id ? [selectedManager.supervisor_id] : []),
        supervisor_name: selectedManager.supervisor_name || infoCardData?.supervisor_name || '',
        dob: formatDateForInput(selectedManager.dob || selectedManager.dob),
        aadhaar_authentication: selectedManager.aadhaar_authentication || false,
      });
    }
  }, [selectedManager]);

  // Correct editValues.supervisor_ids once the authoritative multi-manager
  // list has loaded, so it reflects ALL assigned managers rather than just
  // the single legacy supervisor_id used as a placeholder above.
  useEffect(() => {
    if (userManagers.length > 0) {
      setEditValues((prev) => ({
        ...prev,
        supervisor_ids: userManagers.map((m) => m.managerId),
      }));
    }
  }, [userManagers]);
  
  // Fetch user's assigned managers (multi-manager support)
  useEffect(() => {
    const fetchUserManagers = async () => {
      if (selectedManager?.user_id) {
        setIsLoadingUserManagers(true);
        try {
          const response = await fetchUserManagersAPI(selectedManager.user_id);
          if (response.data?.businessStatusCode === 2) {
            setUserManagers(response.data.data || []);
          } else {
            setUserManagers([]);
          }
        } catch (error) {
          console.error("Error fetching user managers:", error);
          setUserManagers([]);
        } finally {
          setIsLoadingUserManagers(false);
        }
      }
    };
    fetchUserManagers();
  }, [selectedManager?.user_id]);

  // Handler to refresh managers after dialog updates
  const handleManagersUpdated = async () => {
    if (selectedManager?.user_id) {
      try {
        const response = await fetchUserManagersAPI(selectedManager.user_id);
        if (response.data?.businessStatusCode === 2) {
          setUserManagers(response.data.data || []);
        }
        // Also refresh member details to update supervisor_name
        const detailsResponse = await fetchMemberDetails({ userId: selectedManager.user_id });
        const responseData = detailsResponse?.data || detailsResponse;
        if (responseData?.userData) {
          setManagerInfo(responseData.userData);
          setMemberDetails(responseData);
        }
      } catch (error) {
        console.error("Error refreshing managers:", error);
      }
    }
  };

  // Fetch supervisors when edit mode is activated
  useEffect(() => {
    const fetchSupervisorsList = async () => {
      if (isEditing && supervisorsList.length === 0 && !isSupervisorsLoading) {
        setIsSupervisorsLoading(true);
        try {
          const payload = {
            entity_type: "user",
            columns: ["user_id", "full_name"],
            parameter: [
              {
                columnName: "status",
                condition: "=",
                value: "ACTIVE",
              },
              {
                columnName: "role_name",
                condition: "=",
                value: "MANAGER",
              },
              {
                columnName: "org_id",
                condition: "=",
                value: orgId,
              },
            ],
          };
          const response = await fetchSupervisors(payload);
          if (response.businessStatusCode === 2) {
            setSupervisorsList(response?.list || []);
          }
        } catch (error) {
          console.error("Error fetching supervisors:", error);
          setSupervisorsList([]);
        } finally {
          setIsSupervisorsLoading(false);
        }
      }
    };
    fetchSupervisorsList();
  }, [isEditing, orgId]);

  const getSupervisedMembers = (managerId) => {
    // If team members do not have supervisor_id, return all
    if (members && members.length > 0 && members[0].supervisor_id === undefined) {
      return members;
    }
    // Otherwise, filter by supervisor_id
    return members?.filter(member => member.supervisor_id === managerId);
  };

  const getManagersList = () => {
    return members?.filter((member) => member.role_name === "MANAGER") || [];
  };

  const handleChangeManagerClick = () => {
    // Disable assign manager mode if it's active
    if (!isChangeManagerMode && isAssignManagerMode) {
      setIsAssignManagerMode(false);
      setSelectedMembersToAssign([]);
      setUnassignedUsers([]);
    }
    setIsChangeManagerMode(!isChangeManagerMode);
    setSelectedMembersForChange([]);
    setNewManagerId("");
  };

  const handleMemberCheckboxChange = (memberId) => {
    setSelectedMembersForChange((prev) => {
      if (prev.includes(memberId)) {
        return prev.filter((id) => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  const handleSelectAllChange = () => {
    if (selectedManager) {
      const supervisedMembers = getSupervisedMembers(selectedManager.user_id);
      const allIds = supervisedMembers.map((m) => m.user_id);
      if (selectedMembersForChange.length === allIds.length) {
        setSelectedMembersForChange([]);
      } else {
        setSelectedMembersForChange(allIds);
      }
    }
  };

  const handleApplyManagerChange = async () => {
    if (!newManagerId || selectedMembersForChange.length === 0) return;
    try {
      const payload = {
        managerId: newManagerId,
        memberId: selectedMembersForChange,
      };
      await changeManagerAPI(payload);
      setIsChangeManagerMode(false);
      setSelectedMembersForChange([]);
      setNewManagerId("");
      // Only refresh the members list, do NOT clear managerInfo
      if (selectedManager?.user_id) {
        const response = await fetchMemberDetails({ userId: selectedManager.user_id });
        const responseData = response?.data || response;
        let membersList = [];
        if (Array.isArray(responseData?.userData?.supervisedMembers)) {
          membersList = responseData.userData.supervisedMembers;
        } else if (Array.isArray(responseData?.userData?.teamMembers)) {
          membersList = responseData.userData.teamMembers;
        } else {
          membersList = Array.isArray(responseData)
            ? responseData
            : Array.isArray(responseData.members)
              ? responseData.members
              : Array.isArray(responseData.data)
                ? responseData.data
                : [];
        }
        setMembers(membersList);
        setMemberDetails(responseData);
      }
      // Show success toast
      toast.success("Manager changed successfully");
    } catch (error) {
      console.error("Error changing manager:", error);
    }
  };

  // Toggle assign manager mode
  const handleAssignManagerClick = async () => {
    const newMode = !isAssignManagerMode;
    
    // Disable change manager mode if it's active
    if (newMode && isChangeManagerMode) {
      setIsChangeManagerMode(false);
      setSelectedMembersForChange([]);
      setNewManagerId("");
    }
    
    setIsAssignManagerMode(newMode);
    setSelectedMembersToAssign([]);
    
    // Fetch unassigned users when opening assign mode
    if (newMode) {
      setIsLoadingUnassigned(true);
      try {
        const response = await fetchUnassignedUsersAPI(orgId);
        if (response.businessStatusCode === 2) {
          setUnassignedUsers(response.data || []);
        } else {
          toast.error("Failed to fetch unassigned users");
          setUnassignedUsers([]);
        }
      } catch (error) {
        console.error("Error fetching unassigned users:", error);
        toast.error("Failed to fetch unassigned users");
        setUnassignedUsers([]);
      } finally {
        setIsLoadingUnassigned(false);
      }
    }
  };

  // Handle checkbox selection for members to assign
  const handleAssignMemberCheckboxChange = (memberId) => {
    setSelectedMembersToAssign((prev) => {
      if (prev.includes(memberId)) {
        return prev.filter((id) => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  // Get unmanaged members from API response
  const getUnmanagedMembers = () => {
    return unassignedUsers || [];
  };

  // Handle assigning members to manager
  const handleApplyAssignManager = async () => {
    if (selectedMembersToAssign.length === 0) {
      toast.warning("Please select at least one member to assign");
      return;
    }
    if (!selectedManager) {
      toast.warning("No manager selected");
      return;
    }
    
    try {
      const payload = {
        userId: selectedManager.user_id,
        memberIds: selectedMembersToAssign
      };
      
      console.log("Assigning members with payload:", payload);
      
      const response = await assignMembersAPI(payload);
      
      if (response.businessStatusCode === 2) {
        toast.success(response.message || "Members assigned successfully");
        setIsAssignManagerMode(false);
        setSelectedMembersToAssign([]);
        setUnassignedUsers([]);
        // Refresh the members list
        if (selectedManager?.user_id) {
          const response = await fetchMemberDetails({ userId: selectedManager.user_id });
          const responseData = response?.data || response;
          let membersList = [];
          if (Array.isArray(responseData?.userData?.supervisedMembers)) {
            membersList = responseData.userData.supervisedMembers;
          } else if (Array.isArray(responseData?.userData?.teamMembers)) {
            membersList = responseData.userData.teamMembers;
          } else {
            membersList = Array.isArray(responseData)
              ? responseData
              : Array.isArray(responseData.members)
                ? responseData.members
                : Array.isArray(responseData.data)
                  ? responseData.data
                  : [];
          }
          setMembers(membersList);
          setMemberDetails(responseData);
        }
      } else {
        toast.error(response.message || "Failed to assign members");
      }
    } catch (error) {
      console.error("Error assigning members:", error);
      toast.error("Failed to assign members");
    }
  };

  // Cancel assign manager mode
  const handleCancelAssignManager = () => {
    setIsAssignManagerMode(false);
    setSelectedMembersToAssign([]);
    setUnassignedUsers([]);
  };

  const handleEditIconClick = (member, e) => {
    e.stopPropagation();
    // Implement edit logic or navigation
  };

  const handleDeleteIconClick = (member, e) => {
    e.stopPropagation();
    // Implement delete logic
  };

  const handleRowClick = (member) => {
    // Implement row click logic if needed
  };

  if (!selectedManager) {
    return <div className="p-4">No member selected.</div>;
  }


  // For MEMBER and ORG_ADMIN, managerInfo may not be set, so use userData from API
  let infoCardData = managerInfo;
  if ((!infoCardData || !infoCardData.full_name) && memberDetails && memberDetails.userData) {
    infoCardData = memberDetails.userData;
  }
  // Normalize date_of_birth to dob if needed (API may return either field name)
  if (infoCardData && infoCardData.date_of_birth && !infoCardData.dob) {
    infoCardData = { ...infoCardData, dob: infoCardData.date_of_birth };
  }
  const showInfoCard = infoCardData && infoCardData.full_name;
  const supervisedMembers = selectedManager && getSupervisedMembers(selectedManager.user_id);
  const hasSupervisedMembers = supervisedMembers && supervisedMembers.length > 0;

  // Edit form handlers
  const handleEditButtonClick = () => {
    setIsEditing(true);
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setEditValues((prev) => ({ ...prev, [name]: value }));
  };
  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset to original values
    setEditValues({
      full_name: selectedManager.full_name || '',
      email: selectedManager.email || '',
      mobile: selectedManager.mobile || '',
      designation: selectedManager.designation || '',
      org_name: selectedManager.org_name || '',
      role_name: selectedManager.role_name || '',
      supervisor_ids: selectedManager.supervisor_ids && Array.isArray(selectedManager.supervisor_ids)
        ? selectedManager.supervisor_ids
        : (selectedManager.supervisor_id ? [selectedManager.supervisor_id] : []),
      supervisor_name: selectedManager.supervisor_name || infoCardData?.supervisor_name || '',
      dob: formatDateForInput(selectedManager.dob || selectedManager.dob),
      aadhaar_authentication: selectedManager.aadhaar_authentication || false,
    });
  };
  const handleSaveEdit = () => {
    // Call updateMemberAPI as previously used
    if (!selectedManager) return;
    const payload = {
      entity_type: "user",
      comment: "Profile updated",
      old_data: {
        full_name: selectedManager?.full_name || "",
        email: selectedManager?.email || "",
        mobile: selectedManager?.mobile || "",
        employee_id: selectedManager?.employee_id || "",
        designation: selectedManager?.designation || "",
        creation_date_and_time: selectedManager?.creation_date_and_time || "",
        role_name: selectedManager?.role_name || "",
        user_id: selectedManager?.user_id || "",
        org_id: selectedManager?.org_id || "",
        role_id: selectedManager?.role_id || "",
        supervisor_id: selectedManager?.supervisor_id || infoCardData?.supervisor_id || "",
        supervisor_ids: userManagers.length > 0
          ? userManagers.map((m) => m.managerId)
          : (selectedManager?.supervisor_ids || (selectedManager?.supervisor_id ? [selectedManager.supervisor_id] : [])),
        dob: selectedManager?.dob || "",
        aadhaar_authentication: selectedManager?.aadhaar_authentication || false,
      },
      new_data: {
        full_name: editValues.full_name,
        email: editValues.email,
        mobile: editValues.mobile,
        employee_id: selectedManager.employee_id,
        designation: editValues.designation || "",
        creation_date_and_time: selectedManager.creation_date_and_time,
        role_name: editValues.role_name,
        user_id: selectedManager.user_id,
        org_id: selectedManager.org_id,
        role_id: selectedManager.role_id,
        // Prefer the authoritative multi-manager list (userManagers) over
        // editValues.supervisor_ids directly: editValues is only synced from
        // userManagers by an effect, so a save fired before that effect has
        // run (or before fetchUserManagersAPI resolves) would otherwise still
        // submit the 1-manager placeholder and wipe out the rest.
        supervisor_id: (userManagers.length > 0
          ? userManagers.map((m) => m.managerId)
          : editValues.supervisor_ids)[0] || "",
        supervisor_ids: userManagers.length > 0
          ? userManagers.map((m) => m.managerId)
          : editValues.supervisor_ids,
        dob: editValues.dob || "",
        aadhaar_authentication: editValues.aadhaar_authentication || false,
      }
    };
    updateMemberAPI(payload)
      .then((result) => {
        // Check if backend actually succeeded (BusinessSuccess=2, BusinessFailure=1)
        if (result && result.businessStatusCode !== undefined && result.businessStatusCode !== 2) {
          // Backend returned a failure
          toast.error(result.message || 'Update failed');
          setIsEditing(false);
          return;
        }
        // Show success toast
        if (result && result.message) {
          toast.success(result.message);
        }
        // Update ALL local state sources so infoCardData reflects changes immediately
        setSelectedManager((prev) => ({
          ...prev,
          ...payload.new_data
        }));
        // Update managerInfo (used for MANAGER/ADMIN roles)
        setManagerInfo((prev) => prev ? ({
          ...prev,
          ...payload.new_data
        }) : null);
        // Update memberDetails.userData (used for MEMBER role where managerInfo is null)
        setMemberDetails((prev) => prev && prev.userData ? ({
          ...prev,
          userData: { ...prev.userData, ...payload.new_data }
        }) : prev);
        setIsEditing(false);
      })
      .catch((error) => {
        // Optionally show error toast
        if (window && window.toast && typeof window.toast.error === 'function') {
          window.toast.error('Update failed');
        } else if (typeof toast === 'function' || (toast && typeof toast.error === 'function')) {
          toast.error('Update failed');
        }
        setIsEditing(false);
      });
  };

  return (
    <div className="container-fluid p-0 m-0" style={{ background: '#f8f9fa', minHeight: '100vh' }}>
      <div className="p-3">
        {/* Header with Back Button */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="m-0" style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '22px' }}>
            Member Details
          </h4>
          <button
            className="btn"
            onClick={() => navigate(-1)}
            style={{
              fontSize: '14px',
              padding: '8px 20px',
              borderRadius: '6px',
              fontWeight: 1000,
              backgroundColor: 'white',
              color: '#495057',
              border: '1px solid #ced4da',
              transition: 'all 0.2s ease'
            }}
          >
            Back
          </button>
        </div>

        {/* Main Info Card Section */}
        {showInfoCard && (
          <div className="mb-3">
            <div
              className="bg-white shadow-sm"
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '20px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
              }}
            >
              <div className="d-flex flex-row align-items-start gap-3">
                {/* Avatar Section */}
                <div className="d-flex flex-column align-items-center" style={{ minWidth: 80 }}>
                  <div className="rounded-circle bg-gray-200 d-flex align-items-center justify-content-center" style={{ width: 80, height: 80, fontSize: 28, fontWeight: 600, color: '#555' }}>
                    {infoCardData?.user_image ? (
                      <img
                        src={`data:image/jpeg;base64,${infoCardData.user_image}`}
                        alt={infoCardData.full_name}
                        style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      infoCardData?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                    )}
                  </div>
                </div>

                {/* Info Section - Compact Grid */}
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h5 className="m-0" style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '18px' }}>
                      {infoCardData.full_name}
                    </h5>
                    {!isEditing && (
                      <button
                        className="btn btn-sm"
                        onClick={isSuspended ? undefined : handleEditButtonClick}
                        disabled={isSuspended}
                        title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                        style={{
                          fontSize: '13px',
                          padding: '6px 16px',
                          borderRadius: '5px',
                          fontWeight: 1000,
                          backgroundColor: isSuspended ? '#ccc' : '#007bff',
                          color: 'white',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          cursor: isSuspended ? 'not-allowed' : 'pointer',
                          opacity: isSuspended ? 0.6 : 1,
                        }}
                      >
                        <Edit style={{ width: '14px', height: '14px' }} />
                        Edit
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div>
                      <form className="row g-2">
                        <div className="col-md-6">
                          <input
                            type="text"
                            name="full_name"
                            value={editValues.full_name}
                            onChange={handleInputChange}
                            placeholder="Full Name"
                            className="form-control form-control-sm"
                            style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '5px' }}
                          />
                        </div>
                        <div className="col-md-6">
                          <input
                            type="email"
                            name="email"
                            value={editValues.email}
                            onChange={handleInputChange}
                            placeholder="Email"
                            className="form-control form-control-sm"
                            style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '5px' }}
                          />
                        </div>
                        <div className="col-md-6">
                          <input
                            type="text"
                            name="mobile"
                            value={editValues.mobile}
                            onChange={handleInputChange}
                            placeholder="Mobile"
                            className="form-control form-control-sm"
                            style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '5px' }}
                          />
                        </div>
                        <div className="col-md-6">
                          <input
                            type="date"
                            name="dob"
                            value={editValues.dob}
                            onChange={handleInputChange}
                            placeholder="Date of Birth"
                            className="form-control form-control-sm"
                            style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '5px' }}
                          />
                        </div>
                        <div className="col-md-6">
                          <input
                            type="text"
                            name="designation"
                            value={editValues.designation}
                            onChange={handleInputChange}
                            placeholder="Designation"
                            className="form-control form-control-sm"
                            style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '5px' }}
                          />
                        </div>
                        <div className="col-md-6">
                          <FormControl fullWidth size="small" disabled={isSupervisorsLoading}>
                            <MuiSelect
                              multiple
                              value={editValues.supervisor_ids}
                              onChange={(e) => {
                                setEditValues(prev => ({ ...prev, supervisor_ids: e.target.value }));
                              }}
                              input={<OutlinedInput />}
                              displayEmpty
                              renderValue={(selected) => {
                                if (!selected || selected.length === 0) {
                                  return <span style={{ color: '#9ca3af', fontSize: '13px' }}>
                                    {isSupervisorsLoading ? 'Loading managers...' : 'Select Manager(s) (Optional)'}
                                  </span>;
                                }
                                return (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                      const supervisor = supervisorsList.find(s => s.user_id === value);
                                      return (
                                        <Chip
                                          key={value}
                                          label={supervisor?.full_name || value}
                                          size="small"
                                          sx={{
                                            backgroundColor: '#ff7a00',
                                            color: 'white',
                                            '& .MuiChip-deleteIcon': { color: 'white' }
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                );
                              }}
                              MenuProps={{
                                PaperProps: {
                                  style: { maxHeight: 250 },
                                },
                              }}
                              sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#e5e7eb',
                                  borderWidth: '1.5px',
                                  borderRadius: '5px',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#ff7a00',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#ff7a00',
                                  boxShadow: '0 0 0 3px rgba(255, 122, 0, 0.1)',
                                },
                                fontSize: '13px',
                              }}
                            >
                              {supervisorsList.length > 0 ? (
                                supervisorsList.map((supervisor) => (
                                  <MenuItem key={supervisor.user_id} value={supervisor.user_id}>
                                    <Checkbox
                                      checked={editValues.supervisor_ids.includes(supervisor.user_id)}
                                      sx={{ '&.Mui-checked': { color: '#ff7a00' } }}
                                    />
                                    <ListItemText primary={supervisor.full_name || "Unnamed Supervisor"} />
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem disabled>No supervisor available</MenuItem>
                              )}
                            </MuiSelect>
                          </FormControl>
                          {editValues.supervisor_ids.length > 0 && (
                            <div style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
                              Selected: {editValues.supervisor_ids.length} supervisor(s)
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <div className="d-flex align-items-center" style={{ height: '100%' }}>
                            <label className="d-flex align-items-center gap-2" style={{ fontSize: '13px', cursor: 'pointer', marginBottom: 0 }}>
                              <input
                                type="checkbox"
                                name="aadhaar_authentication"
                                checked={editValues.aadhaar_authentication}
                                onChange={(e) => setEditValues(prev => ({ ...prev, aadhaar_authentication: e.target.checked }))}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                              />
                              <span style={{ fontWeight: 1000, color: '#495057' }}>Aadhaar Authentication</span>
                            </label>
                          </div>
                        </div>
                        <div className="col-12 mt-3">
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              onClick={isSuspended ? undefined : handleSaveEdit}
                              disabled={isSuspended}
                              title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                              style={{ fontSize: '13px', padding: '6px 20px', borderRadius: '5px', fontWeight: 1000, opacity: isSuspended ? 0.5 : 1, cursor: isSuspended ? 'not-allowed' : 'pointer' }}
                            >
                              Save Changes
                            </button>
                            <button type="button" className="btn btn-sm btn-secondary" onClick={handleCancelEdit} style={{ fontSize: '13px', padding: '6px 20px', borderRadius: '5px', fontWeight: 1000 }}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  ) : (
                    <div className="row g-2">
                      <div className="col-md-3 col-6">
                        <div className="d-flex flex-column">
                          <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600,  letterSpacing: '0.3px' }}>Email</span>
                          <span style={{ fontSize: '13px', color: '#212529', marginTop: '2px' }}>{infoCardData.email || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="d-flex flex-column">
                          <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600,  letterSpacing: '0.3px' }}>Mobile</span>
                          <span style={{ fontSize: '13px', color: '#212529', marginTop: '2px' }}>{infoCardData.mobile || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="d-flex flex-column">
                          <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600,  letterSpacing: '0.3px' }}>Date of Birth</span>
                          <span style={{ fontSize: '13px', color: '#212529', marginTop: '2px' }}>
                            {infoCardData.dob ? new Date(infoCardData.dob).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="d-flex flex-column">
                          <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600,  letterSpacing: '0.3px' }}>Designation</span>
                          <span style={{ fontSize: '13px', color: '#212529', marginTop: '2px' }}>{infoCardData.designation || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="d-flex flex-column">
                          <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600,  letterSpacing: '0.3px' }}>Organization</span>
                          <span style={{ fontSize: '13px', color: '#212529', marginTop: '2px' }}>{infoCardData.org_name || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="d-flex flex-column">
                          <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600,  letterSpacing: '0.3px' }}>Role</span>
                          <span style={{ fontSize: '13px', color: '#212529', marginTop: '2px' }}>
                            <span style={{ display: 'inline-block', padding: '3px 8px', backgroundColor: '#e7f3ff', color: '#0066cc', borderRadius: '4px', fontSize: '12px', fontWeight: 1000 }}>
                              {infoCardData.role_name}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="d-flex flex-column">
                          <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, letterSpacing: '0.3px' }}>Status</span>
                          <span style={{ fontSize: '13px', color: '#212529', marginTop: '2px' }}>
                            <span style={{ display: 'inline-block', color: infoCardData.status === 'ACTIVE' ? '#155724' : '#e90b0bff', borderRadius: '4px', fontSize: '12px', fontWeight: 1000 }}>
                              {infoCardData.status || 'N/A'}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="d-flex flex-column">
                          <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600,  letterSpacing: '0.3px' }}>Verification</span>
                          <span style={{ fontSize: '13px', color: '#212529', marginTop: '2px' }}>
                            {infoCardData.aadhaar_authentication ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#28a745', fontWeight: 1000 }}>
                                <CheckCircle style={{ width: '14px', height: '14px' }} />
                                Verified
                              </span>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#dc3545', fontWeight: 1000 }}>
                                <XCircle style={{ width: '14px', height: '14px' }} />
                                Not Verified
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="d-flex flex-column">
                          <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600,  letterSpacing: '0.3px' }}>Authorized</span>
                          <span style={{ fontSize: '13px', color: '#212529', marginTop: '2px' }}>
                            {infoCardData.is_authorised ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#28a745', fontWeight: 1000 }}>
                                <CheckCircle style={{ width: '14px', height: '14px' }} />
                                Yes
                              </span>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#dc3545', fontWeight: 1000 }}>
                                <XCircle style={{ width: '14px', height: '14px' }} />
                                No
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                      {/* Multi-Manager Section - Only show for MEMBER role */}
                      {(infoCardData?.role_name === "MEMBER" || selectedManager?.role_name === "MEMBER") && (
                      <div className="col-12 mt-3">
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #e9ecef'
                        }}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span style={{ fontSize: '12px', color: '#495057', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Users size={14} />
                              Assigned Managers ({userManagers.length})
                            </span>
                            <button
                              className="btn btn-sm"
                              onClick={isSuspended ? undefined : () => setIsMultiManagerDialogOpen(true)}
                              disabled={isSuspended}
                              title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                              style={{
                                fontSize: '12px',
                                padding: '4px 12px',
                                borderRadius: '4px',
                                fontWeight: 1000,
                                backgroundColor: isSuspended ? '#ccc' : '#007bff',
                                color: 'white',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                cursor: isSuspended ? 'not-allowed' : 'pointer',
                                opacity: isSuspended ? 0.6 : 1,
                              }}
                            >
                              <UserPlus size={12} />
                              Manage
                            </button>
                          </div>

                          {isLoadingUserManagers ? (
                            <div style={{ fontSize: '12px', color: '#6c757d' }}>Loading managers...</div>
                          ) : userManagers.length === 0 ? (
                            <div style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>
                              No managers assigned. Click "Manage" to add managers.
                            </div>
                          ) : (
                            <div className="d-flex flex-wrap gap-2">
                              {userManagers.map((manager) => (
                                <Tooltip
                                  key={manager.userManagerId}
                                  title={manager.isPrimary ? "Primary Manager" : "Secondary Manager"}
                                  arrow
                                >
                                  <Chip
                                    icon={manager.isPrimary ? <Star size={12} style={{ color: '#ffc107' }} /> : null}
                                    label={manager.managerName}
                                    size="small"
                                    variant={manager.isPrimary ? "filled" : "outlined"}
                                    sx={{
                                      fontSize: '11px',
                                      fontWeight: 1000,
                                      backgroundColor: manager.isPrimary ? '#fff3cd' : 'transparent',
                                      borderColor: manager.isPrimary ? '#ffc107' : '#dee2e6',
                                      '& .MuiChip-icon': { marginLeft: '4px' }
                                    }}
                                  />
                                </Tooltip>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Work Schedule Assignment Section */}
        {memberDetails?.workScheduleAssignment && memberDetails.workScheduleAssignment.length > 0 && (
          <div className="mb-3">
            <div className="bg-white shadow-sm" style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
              <div
                className="px-3 py-2 d-flex align-items-center justify-content-between"
                style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef', cursor: 'pointer' }}
                onClick={() => setWorkScheduleCollapsed((prev) => !prev)}
              >
                <h6 className="m-0" style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>Work Schedule and Location Assignment</h6>
                <button
                  type="button"
                  className="btn btn-link p-0 m-0"
                  style={{ fontSize: '18px', color: '#007bff', textDecoration: 'none', outline: 'none', boxShadow: 'none' }}
                  tabIndex={-1}
                  aria-label={workScheduleCollapsed ? 'Expand section' : 'Collapse section'}
                  onClick={e => { e.stopPropagation(); setWorkScheduleCollapsed((prev) => !prev); }}
                >
                  {workScheduleCollapsed ? <span style={{ fontWeight: 700 }}>&#x25BC;</span> : <span style={{ fontWeight: 700 }}>&#x25B2;</span>}
                </button>
              </div>
              {!workScheduleCollapsed && (
                <div className="p-3">
                  {memberDetails.workScheduleAssignment.map((assignment, idx) => (
                    <div key={idx} className="mb-3">
                      {/* Locations - Compact */}
                      {assignment.workSchedule?.location && assignment.workSchedule.location.length > 0 && (
                        <div className="mb-3">
                          <div className="mb-2" style={{ fontSize: '12px', fontWeight: 600, color: '#495057' }}>Work Locations</div>
                          <div className="row g-2">
                            {assignment.workSchedule.location.map((loc, lIdx) => (
                              <div key={lIdx} className="col-md-6">
                                <div style={{ padding: '10px', backgroundColor: '#fff9f0', border: '1px solid #ffe0b2', borderRadius: '6px' }}>
                                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#1a1a1a', marginBottom: '4px' }}>{loc.location_name}</div>
                                  <div style={{ fontSize: '11px', color: '#6c757d' }}>
                                    Lat: {loc.latitude}, Lng: {loc.longitude} • Radius: {loc.radius}m
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Schedule Info */}
                      <div className="row g-2 mb-3">
                        <div className="col-md-3 col-6">
                          <div className="d-flex flex-column">
                            <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600}}>Schedule Name</span>
                            <span style={{ fontSize: '13px', color: '#212529', marginTop: '2px', fontWeight: 1000 }}>{assignment.work_schedule_name}</span>
                          </div>
                        </div>
                        <div className="col-md-3 col-6">
                          <div className="d-flex flex-column">
                            <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>Start Date</span>
                            <span style={{ fontSize: '13px', color: '#212529', marginTop: '2px' }}>{new Date(assignment.start_date).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="col-md-3 col-6">
                          <div className="d-flex flex-column">
                            <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>End Date</span>
                            <span style={{ fontSize: '13px', color: '#212529', marginTop: '2px' }}>{new Date(assignment.end_date).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="col-md-3 col-6">
                          <div className="d-flex flex-column">
                            <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>Work Arrangement</span>
                            <span style={{ fontSize: '13px', color: '#212529', marginTop: '2px' }}>
                              <span style={{ display: 'inline-block', padding: '3px 8px', backgroundColor: '#e7f3ff', color: '#0066cc', borderRadius: '4px', fontSize: '12px', fontWeight: 1000 }}>
                                {assignment.workSchedule?.work_arrangement || 'N/A'}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                      {/* Weekly Schedule - Compact Grid */}
                      {assignment.workSchedule?.schedule && (
                        <div className="mb-3">
                          <div className="mb-2" style={{ fontSize: '12px', fontWeight: 600, color: '#495057' }}>Weekly Schedule</div>
                          <div className="row g-2">
                            {(() => {
                              // Define the calendar order for days (Monday to Sunday)
                              const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                              // Sort the schedule entries according to calendar order
                              const sortedDays = Object.entries(assignment.workSchedule.schedule).sort(([dayA], [dayB]) => {
                                return dayOrder.indexOf(dayA) - dayOrder.indexOf(dayB);
                              });
                              return sortedDays.map(([day, daySchedule]) => (
                                <div key={day} className="col-md-6 col-lg-4">
                                  <div style={{ padding: '10px', backgroundColor: daySchedule.rest_day ? '#f8f9fa' : '#e7f9f5', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#1a1a1a', marginBottom: '4px' }}>{day}</div>
                                    {daySchedule.rest_day ? (
                                      <span style={{ fontSize: '11px', color: '#6c757d', fontStyle: 'italic' }}>Rest Day</span>
                                    ) : (
                                      daySchedule.slots?.map((slot, sIdx) => (
                                        <div key={sIdx} style={{ fontSize: '11px', color: '#495057' }}>
                                          {slot.start_time} - {slot.end_time}
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                      )}
                      {/* Policies - Compact Grid */}
                      {assignment.workSchedule?.policies && (
                        <div>
                          <div className="mb-2" style={{ fontSize: '12px', fontWeight: 600, color: '#495057' }}>Policies</div>
                          <div className="row g-2">
                            <div className="col-md-4 col-6">
                              <div style={{ padding: '8px', backgroundColor: assignment.workSchedule.policies.app_usage_tracking ? '#d4edda' : '#f8d7da', border: '1px solid', borderColor: assignment.workSchedule.policies.app_usage_tracking ? '#c3e6cb' : '#f5c6cb', borderRadius: '5px' }}>
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>App Usage</div>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: assignment.workSchedule.policies.app_usage_tracking ? '#155724' : '#721c24' }}>
                                  {assignment.workSchedule.policies.app_usage_tracking ? 'Enabled' : 'Disabled'}
                                </div>
                              </div>
                            </div>
                            <div className="col-md-4 col-6">
                              <div style={{ padding: '8px', backgroundColor: assignment.workSchedule.policies.location_tracking ? '#d4edda' : '#f8d7da', border: '1px solid', borderColor: assignment.workSchedule.policies.location_tracking ? '#c3e6cb' : '#f5c6cb', borderRadius: '5px' }}>
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>Location Tracking</div>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: assignment.workSchedule.policies.location_tracking ? '#155724' : '#721c24' }}>
                                  {assignment.workSchedule.policies.location_tracking ? 'Enabled' : 'Disabled'}
                                </div>
                              </div>
                            </div>
                            <div className="col-md-4 col-6">
                              <div style={{ padding: '8px', backgroundColor: assignment.workSchedule.policies.store_activity_images ? '#d4edda' : '#f8d7da', border: '1px solid', borderColor: assignment.workSchedule.policies.store_activity_images ? '#c3e6cb' : '#f5c6cb', borderRadius: '5px' }}>
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>Activity Images</div>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: assignment.workSchedule.policies.store_activity_images ? '#155724' : '#721c24' }}>
                                  {assignment.workSchedule.policies.store_activity_images ? 'Enabled' : 'Disabled'}
                                </div>
                              </div>
                            </div>
                            <div className="col-md-4 col-6">
                              <div style={{ padding: '8px', backgroundColor: '#e7f3ff', border: '1px solid #bee5eb', borderRadius: '5px' }}>
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>Geofencing</div>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: '#0066cc' }}>
                                  {assignment.workSchedule.policies.geo_fencing?.enable_geofencing ? 'Enabled' : 'Disabled'}
                                </div>
                              </div>
                            </div>
                            <div className="col-md-4 col-6">
                              <div style={{ padding: '8px', backgroundColor: '#fff3cd', border: '1px solid #ffeeba', borderRadius: '5px' }}>
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>Auto Checkout</div>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: '#856404' }}>
                                  {assignment.workSchedule.policies.checkout_policies?.allow_auto_check_out ? 'Allowed' : 'Not Allowed'}
                                </div>
                              </div>
                            </div>
                            <div className="col-md-4 col-6">
                              <div style={{ padding: '8px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '5px' }}>
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>Daily Overtime</div>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: '#495057' }}>
                                  {assignment.workSchedule.policies.overtime_policies?.daily_overtime?.enabled ? 'Enabled' : 'Disabled'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {/* Supervised Members Table - Only show if there are supervised members */}
        {hasSupervisedMembers && (
          <>
            {/* Header */}
            <div className="bg-white shadow-sm mb-2" style={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '12px 16px',
            }}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="m-0" style={{ color: '#1a1a1a', fontSize: '15px', fontWeight: 600 }}>
                    Supervised Members
                  </h6>
                  <span className="text-muted" style={{ fontSize: '12px', fontWeight: '400' }}>
                    Manager: {selectedManager?.full_name || selectedManager?.email}
                  </span>
                </div>
                {hasSupervisedMembers && (
                  <div className="d-flex gap-2">
                    <button
                      onClick={isSuspended ? undefined : handleAssignManagerClick}
                      disabled={(isChangeManagerMode && !isAssignManagerMode) || isSuspended}
                      title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                      style={{
                        height: '34px',
                        padding: '0 16px',
                        fontSize: '13px',
                        fontWeight: '1000',
                        borderRadius: '5px',
                        border: 'none',
                        backgroundColor: isSuspended ? '#ccc' : (isAssignManagerMode ? '#6c757d' : '#28a745'),
                        color: 'white',
                        cursor: ((isChangeManagerMode && !isAssignManagerMode) || isSuspended) ? 'not-allowed' : 'pointer',
                        opacity: ((isChangeManagerMode && !isAssignManagerMode) || isSuspended) ? 0.6 : 1,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      {isAssignManagerMode ? (
                        <>
                          <XCircle style={{ width: '14px', height: '14px' }} />
                          Cancel
                        </>
                      ) : (
                        <>
                          <UserPlus style={{ width: '14px', height: '14px' }} />
                          Add Team Member
                        </>
                      )}
                    </button>
                    <button
                      onClick={isSuspended ? undefined : handleChangeManagerClick}
                      disabled={(isAssignManagerMode && !isChangeManagerMode) || isSuspended}
                      title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                      style={{
                        height: '34px',
                        padding: '0 16px',
                        fontSize: '13px',
                        fontWeight: '1000',
                        borderRadius: '5px',
                        border: 'none',
                        backgroundColor: isSuspended ? '#ccc' : (isChangeManagerMode ? '#6c757d' : '#FF8C00'),
                        color: 'white',
                        cursor: ((isAssignManagerMode && !isChangeManagerMode) || isSuspended) ? 'not-allowed' : 'pointer',
                        opacity: ((isAssignManagerMode && !isChangeManagerMode) || isSuspended) ? 0.6 : 1,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      {isChangeManagerMode ? (
                        <>
                          <XCircle style={{ width: '14px', height: '14px' }} />
                          Cancel
                        </>
                      ) : (
                        <>
                          <Edit style={{ width: '14px', height: '14px' }} />
                          Change Manager
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
            {/* Change Manager Section */}
            {isChangeManagerMode && (
              <div
                className="mb-2 p-3 bg-white shadow-sm"
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                }}
              >
                <h6 className="mb-2" style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a' }}>
                  Assign New Manager
                </h6>
                <div className="row g-2 align-items-end">
                  <div className="col-md-8">
                    <label className="form-label" style={{ fontSize: '12px', fontWeight: 1000, color: '#6c757d', marginBottom: '6px' }}>
                      Select Manager
                    </label>
                    <div className="position-relative">
                      <select
                        className="form-select form-select-sm"
                        value={newManagerId}
                        onChange={(e) => setNewManagerId(e.target.value)}
                        onFocus={handleManagersDropdownFocus}
                        style={{
                          fontSize: '13px',
                          border: '1px solid #ced4da',
                          borderRadius: '5px',
                          padding: '8px 32px 8px 12px',
                          backgroundColor: 'white',
                          color: '#495057',
                          fontWeight: '400',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          height: '36px',
                        }}
                      >
                        <option value="">Choose a manager...</option>
                        {isManagersLoading && (
                          <option disabled>Loading managers...</option>
                        )}
                        {managersError && (
                          <option disabled>{managersError}</option>
                        )}
                        {managersList
                          .filter((manager) => manager.user_id !== selectedManager?.user_id)
                          .map((manager) => (
                            <option key={manager.user_id} value={manager.user_id}>
                              {manager.full_name || manager.email}
                            </option>
                          ))}
                      </select>
                      {newManagerId && (
                        <CheckCircle
                          className="position-absolute"
                          style={{
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '18px',
                            height: '18px',
                            color: '#28a745',
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <button
                      onClick={isSuspended ? undefined : handleApplyManagerChange}
                      disabled={selectedMembersForChange.length === 0 || !newManagerId || isSuspended}
                      title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                      style={{
                        height: '36px',
                        padding: '0 16px',
                        fontSize: '13px',
                        fontWeight: '1000',
                        borderRadius: '5px',
                        border: 'none',
                        backgroundColor: (selectedMembersForChange.length === 0 || !newManagerId || isSuspended) ? '#e9ecef' : '#28a745',
                        color: (selectedMembersForChange.length === 0 || !newManagerId || isSuspended) ? '#adb5bd' : 'white',
                        cursor: (selectedMembersForChange.length === 0 || !newManagerId || isSuspended) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      <CheckCircle style={{ width: '14px', height: '14px' }} />
                      Apply Changes
                    </button>
                  </div>
                </div>
                <div
                  className="mt-2 p-2 rounded d-flex align-items-center gap-2"
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    fontSize: '12px',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '20px',
                      height: '20px',
                      borderRadius: '10px',
                      backgroundColor: selectedMembersForChange.length > 0 ? '#28a745' : '#adb5bd',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      padding: '0 6px',
                    }}
                  >
                    {selectedMembersForChange.length}
                  </span>
                  <span style={{ color: '#6c757d', fontWeight: '1000' }}>
                    {selectedMembersForChange.length === 0 ? 'No members selected' : `member${selectedMembersForChange.length > 1 ? 's' : ''} selected`}
                  </span>
                </div>
              </div>
            )}
            
            {/* Assign Manager Section */}
            {isAssignManagerMode && (
              <div
                className="mb-2 p-3 bg-white shadow-sm"
                style={{
                  border: '1px solid #28a745',
                  borderRadius: '8px',
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0" style={{ fontSize: '13px', fontWeight: 600, color: '#28a745', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UserPlus size={16} />
                    Assign Members to {selectedManager?.full_name || selectedManager?.email}
                  </h6>
                  <button
                    onClick={isSuspended ? undefined : handleApplyAssignManager}
                    disabled={selectedMembersToAssign.length === 0 || isSuspended}
                    title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                    style={{
                      width: 'auto',
                      height: '32px',
                      padding: '0 16px',
                      fontSize: '12px',
                      fontWeight: '1000',
                      borderRadius: '5px',
                      border: 'none',
                      backgroundColor: (selectedMembersToAssign.length === 0 || isSuspended) ? '#e9ecef' : '#28a745',
                      color: (selectedMembersToAssign.length === 0 || isSuspended) ? '#adb5bd' : 'white',
                      cursor: (selectedMembersToAssign.length === 0 || isSuspended) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <CheckCircle style={{ width: '14px', height: '14px' }} />
                    Assign Members
                  </button>
                </div>
                <div className="mb-3">
                  <div style={{ 
                    maxHeight: '400px', 
                    overflowY: 'auto',
                    border: '1px solid #ced4da',
                    borderRadius: '5px',
                    backgroundColor: 'white',
                  }}>
                    {isLoadingUnassigned ? (
                      <div className="p-3 text-center text-muted" style={{ fontSize: '12px' }}>
                        Loading unassigned members...
                      </div>
                    ) : getUnmanagedMembers().length === 0 ? (
                      <div className="p-3 text-center text-muted" style={{ fontSize: '12px' }}>
                        No unassigned members available
                      </div>
                    ) : (
                      getUnmanagedMembers().map((member) => (
                        <div
                          key={member.user_id}
                          className="p-2 border-bottom d-flex align-items-center"
                          style={{
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                          onClick={() => handleAssignMemberCheckboxChange(member.user_id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedMembersToAssign.includes(member.user_id)}
                            onChange={() => handleAssignMemberCheckboxChange(member.user_id)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer',
                              marginRight: '12px',
                            }}
                          />
                          <div className="flex-grow-1">
                            <div style={{ fontSize: '12px', fontWeight: '1000', color: '#495057' }}>
                              <span style={{ color: '#28a745', marginRight: '8px' }}>{member.role_name || 'N/A'}</span>
                              {member.full_name || member.email}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6c757d' }}>
                              {member.email}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div
                  className="mt-2 mb-2 p-2 rounded d-flex align-items-center gap-2"
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    fontSize: '12px',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '20px',
                      height: '20px',
                      borderRadius: '10px',
                      backgroundColor: selectedMembersToAssign.length > 0 ? '#28a745' : '#adb5bd',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      padding: '0 6px',
                    }}
                  >
                    {selectedMembersToAssign.length}
                  </span>
                  <span style={{ color: '#6c757d', fontWeight: '1000' }}>
                    {selectedMembersToAssign.length === 0 ? 'No members selected' : `member${selectedMembersToAssign.length > 1 ? 's' : ''} selected for assignment`}
                  </span>
                </div>
              </div>
            )}
            
            {/* Supervised Members Table */}
            <div className="bg-white shadow-sm" style={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              {/* Table Header */}
              <div
                className="d-grid align-items-center px-3 py-2"
                style={{
                  display: 'grid',
                  gridTemplateColumns: isChangeManagerMode
                    ? '40px 2fr 2fr 1fr 1fr 1.2fr'
                    : '2fr 2fr 1fr 1fr 1.2fr',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #e9ecef'
                }}
              >
                {isChangeManagerMode && (
                  <div className="d-flex align-items-center justify-content-center">
                    <div
                      onClick={handleSelectAllChange}
                      style={{
                        width: 18,
                        height: 18,
                        border: '2px solid #FF8C00',
                        borderRadius: 3,
                        background: selectedMembersForChange.length === supervisedMembers.length && supervisedMembers.length > 0 ? '#FF8C00' : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      title="Select All"
                    >
                      {selectedMembersForChange.length === supervisedMembers.length && supervisedMembers.length > 0 && (
                        <CheckCircle style={{ width: '12px', height: '12px', color: 'white' }} />
                      )}
                    </div>
                  </div>
                )}
                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, letterSpacing: '0.3px' }}>User Name</div>
                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, letterSpacing: '0.3px' }}>Email</div>
                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, letterSpacing: '0.3px' }}>Role</div>
                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, letterSpacing: '0.3px' }}>Status</div>
                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, letterSpacing: '0.3px' }}>Verification</div>
              </div>
              {/* Table Body */}
              {supervisedMembers.map((supervisedMember, index) => (
                <div
                  key={supervisedMember.user_id}
                  className="d-grid align-items-center px-3 py-2"
                  onClick={(e) => {
                    if (isChangeManagerMode) {
                      if (e.target.type !== 'checkbox') {
                        handleMemberCheckboxChange(supervisedMember.user_id);
                      }
                    } else {
                      handleRowClick(supervisedMember);
                    }
                  }}
                  style={{
                    cursor: 'pointer',
                    gridTemplateColumns: isChangeManagerMode
                      ? '40px 2fr 2fr 1fr 1fr 1.2fr'
                      : '2fr 2fr 1fr 1fr 1.2fr',
                    transition: 'all 0.2s ease',
                    backgroundColor: isChangeManagerMode && selectedMembersForChange.includes(supervisedMember.user_id)
                      ? '#fff3cd'
                      : '',
                    borderBottom: index < supervisedMembers.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!isChangeManagerMode || !selectedMembersForChange.includes(supervisedMember.user_id)) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isChangeManagerMode && selectedMembersForChange.includes(supervisedMember.user_id)) {
                      e.currentTarget.style.backgroundColor = '#fff3cd';
                    } else {
                      e.currentTarget.style.backgroundColor = '';
                    }
                  }}
                >
                  {/* Checkbox Column (shown only in change manager mode) */}
                  {isChangeManagerMode && (
                    <div className="d-flex align-items-center justify-content-center">
                      <div
                        onClick={(e) => { e.stopPropagation(); handleMemberCheckboxChange(supervisedMember.user_id); }}
                        style={{
                          width: 18,
                          height: 18,
                          border: '2px solid #FF8C00',
                          borderRadius: 3,
                          background: selectedMembersForChange.includes(supervisedMember.user_id) ? '#FF8C00' : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        {selectedMembersForChange.includes(supervisedMember.user_id) && (
                          <CheckCircle style={{ width: '12px', height: '12px', color: 'white' }} />
                        )}
                      </div>
                    </div>
                  )}
                  {/* Name Column */}
                  <div className="d-flex align-items-center" style={{ overflow: 'hidden' }}>
                    <div style={{ overflow: 'hidden', minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: '13px',
                          fontWeight: 1000,
                          color: '#212529',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={supervisedMember.full_name && supervisedMember.full_name.trim() !== "" && supervisedMember.full_name !== "null"
                          ? supervisedMember.full_name
                          : supervisedMember.email || supervisedMember.user_id}
                      >
                        {supervisedMember.full_name && supervisedMember.full_name.trim() !== "" && supervisedMember.full_name !== "null"
                          ? supervisedMember.full_name
                          : supervisedMember.email || supervisedMember.user_id}
                      </div>
                    </div>
                  </div>
                  {/* Email Column */}
                  <div
                    style={{
                      fontSize: '13px',
                      color: '#6c757d',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                    title={supervisedMember.email}
                  >
                    {supervisedMember.email}
                  </div>
                  {/* Role Column */}
                  <div style={{ whiteSpace: 'nowrap' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '3px 8px',
                        backgroundColor: '#e7f3ff',
                        color: '#0066cc',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 1000
                      }}
                    >
                      {supervisedMember.role_name}
                    </span>
                  </div>
                  {/* Status Column */}
                  <div style={{ whiteSpace: 'nowrap' }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 1000,
                        backgroundColor: supervisedMember.status === "ACTIVE" ? '#d4edda' : '#f8d7da',
                        color: supervisedMember.status === "ACTIVE" ? '#155724' : '#721c24',
                      }}
                    >
                      {supervisedMember.status === "ACTIVE" ? "Active" : "Inactive"}
                    </span>
                  </div>
                  {/* Identification Column */}
                  <div className="d-flex align-items-center gap-1" style={{ whiteSpace: 'nowrap' }}>
                    {supervisedMember.is_authorised === true ? (
                      <>
                        <CheckCircle style={{ width: '14px', height: '14px', color: '#28a745', flexShrink: 0 }} />
                        <span style={{ fontSize: '12px', color: '#28a745', fontWeight: 1000 }}>Verified</span>
                      </>
                    ) : (
                      <>
                        <XCircle style={{ width: '14px', height: '14px', color: '#dc3545', flexShrink: 0 }} />
                        <span style={{ fontSize: '12px', color: '#dc3545', fontWeight: 1000 }}>Unverified</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Multi-Manager Dialog */}
      <MultiManagerDialog
        open={isMultiManagerDialogOpen}
        onClose={() => setIsMultiManagerDialogOpen(false)}
        userId={selectedManager?.user_id}
        userName={selectedManager?.full_name || infoCardData?.full_name || 'User'}
        orgId={orgId}
        onUpdate={handleManagersUpdated}
      />
    </div>
  );
};

export default MemberDetailsPage;
